/**
 * telefon.js — telefon girdisinin saf mantığı (backlog #746). Arayüz: Components/Common/TelefonGirdisi.jsx.
 *
 * Kutu iki parçadır: ülke arama kodu (+90) ve ulusal numara (5323064970). Forma TEK değer verilir, arka ucun sakladığı biçimde
 * (sms/PhoneNumber.storageForm, #633): Türkiye için 10 hane ("5323064970"), diğer ülkeler için "+<kod><numara>" ("+491701234567").
 * Arka uç "+90" ve "5323064970"'ı olduğu gibi kabul eder; bu yüzden arka uçta değişiklik gerekmez.
 *
 * Uzunluk: +90 için TAM 10 hane (yazılan baştaki 0 atılır); başka kod için kod + numara en çok 15 hane (E.164) ve en az 8 hane.
 * Önek (50x/53x/54x/55x ...) burada DENETLENMEZ: o liste satıcıya bağlı ve arka ucun tek regex'inde duruyor (PhoneNumber.VENDOR_MOBILE);
 * kopyası burada eskirse geçerli bir numarayı engellerdi. Yeni bir değer arka uçta reddedilirse sunucunun iletisi görünür.
 *
 * ESKİ NUMARALAR (madde 3): çözülemeyen bir kayıtlı değer (harfli, kısa, bilinmeyen kodlu) `eski` olarak taşınır, olduğu gibi
 * gösterilir ve çağırana GERİ VERİLEN değer değişmez — başka bir alan düzenlenirken aynen yeniden gider (arka uç değişmemiş değeri hiç
 * reddetmez). Yalnızca biri numara kutusuna yazarsa yeni bir değere dönüşür.
 */
import { BILINEN_KODLAR, ISO_KODU, kodunIlkUlkesi } from "./ulkeTelefonKodlari";

export const TR_KODU = "90";
export const TR_HANE = 10;
export const E164_EN_FAZLA = 15; // kod + numara
export const E164_EN_AZ = 8;

/** Başlangıç durumu: Türkiye, boş numara. */
export const bosDurum = () => ({ kod: TR_KODU, iso: "TR", ulusal: "", eski: null });

/** Bu kodla ulusal numaraya sığan en çok hane: +90 için 10, diğerleri için 15 − kod uzunluğu. */
export const ulusalEnFazla = (kod) => (kod === TR_KODU ? TR_HANE : E164_EN_FAZLA - String(kod).length);

const rakamlar = (s) => String(s ?? "").replace(/\D/g, "");

/** Metnin başındaki 1-3 hane bilinen bir ülke koduysa onu verir (kodlar önek içermediği için tek sonuç), yoksa null. */
export function kodBul(haneler) {
  for (let n = 1; n <= 3; n += 1) {
    const aday = haneler.slice(0, n);
    if (BILINEN_KODLAR.has(aday)) return aday;
  }
  return null;
}

/** Durumu kod + ulusal numara olarak kurar. ISO yalnızca gösterim içindir (kodu paylaşan ülkeler: +1, +7); verilen ISO bu kodu taşımıyorsa kodun ilk ülkesi alınır. */
function durum(kod, ulusal, iso) {
  return { kod, iso: iso && ISO_KODU.get(iso) === kod ? iso : kodunIlkUlkesi(kod), ulusal: ulusal.slice(0, ulusalEnFazla(kod)), eski: null };
}

/**
 * Kayıtlı / dışarıdan gelen bir değeri duruma çevirir. Aynı kuralı arka uç `PhoneNumber.analyse` uygular: boşluk, tire, nokta, parantez
 * yok sayılır; "+" ya da "00" açık ülke kodudur; kodsuz yazıda 10 hane, 0 + 10 hane ya da 90 + 10 hane Türkiye'dir.
 * Çözülemeyen değer `eski` olarak döner (olduğu gibi tutulur).
 */
export function telefonCoz(deger) {
  const metin = String(deger ?? "").trim();
  if (!metin) return bosDurum();
  const eski = () => ({ ...bosDurum(), eski: String(deger) });
  if (!/^\+?[\d\s().-]+$/.test(metin)) return eski(); // harf, eğik çizgi vb.: kayıt olduğu gibi kalsın
  let hane = rakamlar(metin);
  if (!hane) return eski();
  let acik = metin.startsWith("+");
  if (!acik && hane.startsWith("00")) { acik = true; hane = hane.slice(2); }
  if (acik) {
    if (hane.startsWith(TR_KODU)) {
      return hane.length === 2 + TR_HANE ? durum(TR_KODU, hane.slice(2)) : eski();
    }
    const kod = kodBul(hane);
    if (!kod || hane.startsWith("0") || hane.length < E164_EN_AZ || hane.length > E164_EN_FAZLA) return eski();
    return durum(kod, hane.slice(kod.length));
  }
  if (hane.startsWith(TR_KODU) && hane.length === 2 + TR_HANE) return durum(TR_KODU, hane.slice(2));
  if (hane.startsWith("0") && hane.length === 1 + TR_HANE) return durum(TR_KODU, hane.slice(1));
  if (hane.length === TR_HANE) return durum(TR_KODU, hane);
  return eski();
}

/** Forma verilen tek değer: eski ise aynen eski metin, boş numara boş metin, Türkiye 10 hane, diğerleri "+<kod><numara>". */
export function telefonBirlestir(d) {
  if (d.eski != null) return d.eski;
  if (!d.ulusal) return "";
  return d.kod === TR_KODU ? d.ulusal : `+${d.kod}${d.ulusal}`;
}

/**
 * Numara kutusuna bir metin girildi (yazma, yapıştırma, otomatik doldurma). Yapıştırılan "+90 532 306 49 70", "0532 ...", "0090 ..." ve
 * "905323064970" kod ve numaraya kendiliğinden bölünür; "+49 170 ..." seçiciyi Almanya'ya çevirir. Kutu yalnızca rakam tutar.
 */
export function numaraGirildi(d, metin) {
  const s = String(metin ?? "").trim();
  let hane = rakamlar(s);
  let acik = s.startsWith("+");
  if (!acik && s.startsWith("00") && hane.length > 2) { acik = true; hane = hane.slice(2); }
  const mevcutKod = d.kod;
  if (acik && hane) {
    if (hane.startsWith(TR_KODU)) return durum(TR_KODU, hane.slice(2).replace(/^0+/, ""));
    const kod = kodBul(hane);
    if (kod) return durum(kod, hane.slice(kod.length), kod === mevcutKod ? d.iso : undefined);
  }
  if (mevcutKod === TR_KODU) {
    if (hane.length === 2 + TR_HANE && hane.startsWith(TR_KODU)) hane = hane.slice(2); // "905323064970" yapıştırıldı
    hane = hane.replace(/^0+/, ""); // yazılan baştaki 0 atılır
  }
  return durum(mevcutKod, hane, d.iso);
}

/** Seçicide başka bir ülke seçildi; numara yeni kodun sınırına kısalır. Eski (çözülemeyen) değerde başka koda geçmek numarayı sıfırlar. */
export function kodSecildi(d, iso, kod) {
  if (d.eski != null) return kod === TR_KODU ? d : { kod, iso, ulusal: "", eski: null };
  return { kod, iso, ulusal: d.ulusal.slice(0, ulusalEnFazla(kod)), eski: null };
}

/**
 * "bos" | "eksik" | "tam" | "eski". +90: tam = 10 hane. Diğer kodlar: kod + numara toplamı 8..15 hane.
 * Yalnızca uzunluğa bakar (önek denetimi için bkz. dosya başı).
 */
export function telefonDurumu(d) {
  if (d.eski != null) return "eski";
  if (!d.ulusal) return "bos";
  if (d.kod === TR_KODU) return d.ulusal.length === TR_HANE ? "tam" : "eksik";
  return d.kod.length + d.ulusal.length >= E164_EN_AZ ? "tam" : "eksik";
}
